/**
 * 🛡️ IMMUTABLE: Profile Sync Service
 *
 * ⚠️ CRITICAL: Read docs/IMMUTABLE_PROFILE_SYNC.md before ANY modifications!
 * This module has been broken multiple times. Changes require explicit approval.
 *
 * Provides reliable synchronization between web-app and Chrome extension
 * with deduplication and error handling
 */

interface ProfileSyncMessage {
  type: 'PROFILE_SAVED' | 'PROFILE_CANCELLED' | 'PROFILE_DELETED';
  timestamp: number;
  id?: string;
  data?: any;
}

class ProfileSyncService {
  private static instance: ProfileSyncService;
  private lastMessageTimestamp: number = 0;
  private readonly MESSAGE_ID_PREFIX = 'profile-sync';

  static getInstance(): ProfileSyncService {
    if (!ProfileSyncService.instance) {
      ProfileSyncService.instance = new ProfileSyncService();
    }
    return ProfileSyncService.instance;
  }

  /**
   * Generate unique message ID
   */
  private generateMessageId(type: ProfileSyncMessage['type']): string {
    return `${this.MESSAGE_ID_PREFIX}-${type}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  /**
   * Check if message should be sent (throttling)
   */
  private shouldSendMessage(): boolean {
    const now = Date.now();
    // Don't send more than one message per second
    if (now - this.lastMessageTimestamp < 1000) {
      return false;
    }
    this.lastMessageTimestamp = now;
    return true;
  }

  /**
   * 🛡️ IMMUTABLE: Send message to extension via localStorage with deduplication
   * This implementation is PROVEN to work. DO NOT modify without extensive testing!
   */
  private sendLocalStorageMessage(message: ProfileSyncMessage): void {
    if (!this.shouldSendMessage()) {
      console.log('⚠️ [ProfileSync] Message throttled:', message.type);
      return;
    }

    // Defensive: Validate message structure
    if (!message || !message.type) {
      console.error('❌ [ProfileSync] Cannot send invalid message:', message);
      return;
    }

    // Ensure message has timestamp and unique ID
    if (!message.timestamp) {
      message.timestamp = Date.now();
    }
    if (!message.id) {
      message.id = this.generateMessageId(message.type);
    }

    try {
      // Defensive: Check localStorage availability
      if (typeof localStorage === 'undefined') {
        console.log('⚠️ [ProfileSync] localStorage not available');
        return;
      }

      // Defensive: Don't overwrite newer message
      const existing = localStorage.getItem('profile-sync-message');
      if (existing) {
        try {
          const parsed = JSON.parse(existing);
          if (parsed.timestamp && parsed.timestamp > message.timestamp) {
            console.log('⚠️ [ProfileSync] Not overwriting newer message');
            return;
          }
        } catch (e) {
          // Clean up malformed data
          localStorage.removeItem('profile-sync-message');
        }
      }

      localStorage.setItem('profile-sync-message', JSON.stringify(message));
      console.log('✅ [ProfileSync] Message sent via localStorage:', message.type, 'ID:', message.id);

      // Auto-cleanup after 5 seconds - DO NOT change this timing!
      setTimeout(() => {
        try {
          const stored = localStorage.getItem('profile-sync-message');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.id === message.id) {
              localStorage.removeItem('profile-sync-message');
              console.log('🧹 [ProfileSync] Auto-cleaned message:', message.id);
            }
          }
        } catch (e) {
          // Clean up malformed data
          try {
            localStorage.removeItem('profile-sync-message');
          } catch (cleanupError) {
            console.error('❌ [ProfileSync] Failed to cleanup:', cleanupError);
          }
        }
      }, 5000);
    } catch (error) {
      console.error('❌ [ProfileSync] Failed to send localStorage message:', error);
    }
  }

  /**
   * Send message to extension via Chrome runtime API (if available)
   */
  private async sendRuntimeMessage(message: ProfileSyncMessage): Promise<boolean> {
    // Check if Chrome API is available
    if (!window.chrome?.runtime?.id) {
      console.log('⚠️ [ProfileSync] Chrome runtime not available');
      return false;
    }

    if (!this.shouldSendMessage()) {
      console.log('⚠️ [ProfileSync] Message throttled:', message.type);
      return false;
    }

    // Ensure message has timestamp and unique ID
    if (!message.timestamp) {
      message.timestamp = Date.now();
    }
    if (!message.id) {
      message.id = this.generateMessageId(message.type);
    }

    try {
      const response = await window.chrome.runtime.sendMessage({
        type: 'WEB_APP_PROFILE_SYNC',
        action: message.type,
        timestamp: message.timestamp,
        id: message.id,
        data: message.data
      });

      if (response?.success) {
        console.log('✅ [ProfileSync] Message sent via runtime:', message.type, 'ID:', message.id);
        return true;
      } else {
        console.warn('⚠️ [ProfileSync] Runtime message failed:', response?.error);
        return false;
      }
    } catch (error) {
      console.error('❌ [ProfileSync] Failed to send runtime message:', error);
      return false;
    }
  }

  /**
   * Notify that profile was saved
   */
  public notifyProfileSaved(data?: any): void {
    console.log('📤 [ProfileSync] Notifying profile saved');

    const message: ProfileSyncMessage = {
      type: 'PROFILE_SAVED',
      timestamp: Date.now(),
      data
    };

    // Try runtime first, fallback to localStorage
    this.sendRuntimeMessage(message).catch(() => {
      this.sendLocalStorageMessage(message);
    });
  }

  /**
   * Notify that profile creation was cancelled
   */
  public notifyProfileCancelled(): void {
    console.log('📤 [ProfileSync] Notifying profile cancelled');

    const message: ProfileSyncMessage = {
      type: 'PROFILE_CANCELLED',
      timestamp: Date.now()
    };

    // Try runtime first, fallback to localStorage
    this.sendRuntimeMessage(message).catch(() => {
      this.sendLocalStorageMessage(message);
    });
  }

  /**
   * Notify that profile was deleted
   */
  public notifyProfileDeleted(): void {
    console.log('📤 [ProfileSync] Notifying profile deleted');

    const message: ProfileSyncMessage = {
      type: 'PROFILE_DELETED',
      timestamp: Date.now()
    };

    // Try runtime first, fallback to localStorage
    this.sendRuntimeMessage(message).catch(() => {
      this.sendLocalStorageMessage(message);
    });
  }

  /**
   * Clean up any leftover sync messages
   */
  public cleanup(): void {
    try {
      localStorage.removeItem('profile-sync-message');
      console.log('🧹 [ProfileSync] Cleaned up sync messages');
    } catch (error) {
      console.error('❌ [ProfileSync] Failed to cleanup:', error);
    }
  }
}

// Export singleton instance
export const profileSyncService = ProfileSyncService.getInstance();

// Export type for use in components
export type { ProfileSyncMessage };